'use client';

import { gql, useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import clsx from 'clsx';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { isRequired } from '../../util/formValidators';
import FormError from '../FormError';

interface FormValues {
  name: string;
  email: string;
}

const MidCtaForm = () => {
  const [submitted, setSubmitted] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', email: '' },
  });

  const [reqAddCustomerLeads, { loading, error, data, reset: resetMutation }] = useMutation(gql`
    mutation AddCustomerLeads($input: AddCustomerLeadsInput) {
      addCustomerLeads(input: $input) {
        code
        message
        content
      }
    }
  `);
  const isRequestOk = data ? String(data.addCustomerLeads?.code) === '200' : false;
  const responseError = data && !isRequestOk ? new Error(data.addCustomerLeads?.content || '') : null;

  const onSubmit = values => {
    resetMutation();
    reqAddCustomerLeads({
      variables: {
        input: { name: values.name, email: values.email },
      },
    }).then(({ data }) => {
      if (String(data.addCustomerLeads?.code) === '200') {
        setSubmitted(true);
      }
    });
  };

  if (submitted) {
    return (
      <div className='relative z-[2] mb-2 font-poppins text-sm font-semibold text-[#A8D5AA]'>
        ✓ Check your inbox — your guide is on its way!
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='mx-auto mb-3 grid w-full max-w-[580px] grid-cols-1 items-start gap-3 md_grid-cols-[1fr_1fr_auto]'
      >
        <div className='w-full'>
          <input
            type='text'
            placeholder='First name'
            {...register('name', { validate: isRequired })}
            className={`h-14 w-full min-w-0 rounded-[10px] border-[1.5px] bg-[rgba(255,255,255,0.96)] px-4 font-sans text-base text-[#1a2e1b] outline-none transition-colors focus_border-[#A8D5AA] ${errors.name ? 'border-red' : 'border-[rgba(168,213,170,0.3)]'}`}
          />
          <ErrorMessage
            errors={errors}
            name='name'
            render={({ message }) => <p className='mt-1 text-left text-[13px] text-red'>{message}</p>}
          />
        </div>
        <div className='w-full'>
          <input
            type='email'
            placeholder='Email address'
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[A-Za-zd0-9]+([-_.][A-Za-zd0-9]+)*@([A-Za-zd]+[-.])+[A-Za-zd]{2,5}$/,
                message: 'Invalid email address',
              },
            })}
            className={`h-14 w-full min-w-0 rounded-[10px] border-[1.5px] bg-[rgba(255,255,255,0.96)] px-4 font-sans text-base text-[#1a2e1b] outline-none transition-colors focus_border-[#A8D5AA] ${errors.email ? 'border-red' : 'border-[rgba(168,213,170,0.3)]'}`}
          />
          <ErrorMessage
            errors={errors}
            name='email'
            render={({ message }) => <p className='mt-1 text-left text-[13px] text-red'>{message}</p>}
          />
        </div>
        <button
          type='submit'
          disabled={loading}
          className='relative h-14 cursor-pointer whitespace-nowrap rounded-[10px] border-none bg-white px-10 font-poppins text-[18px] font-semibold text-primary transition-all duration-[250ms] hover_-translate-y-0.5 hover_bg-[#f6f5f3] hover_shadow-[0_8px_24px_rgba(0,0,0,0.2)]'
        >
          <span className={clsx(loading ? 'invisible' : '')}>Get Guide →</span>
          <div
            className={clsx(
              'absolute left-1/2 top-1/2 z-[2] h-auto w-5 -translate-x-1/2 -translate-y-1/2 ',
              loading ? 'block' : 'hidden',
            )}
          >
            <svg
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              className='h-auto w-5 animate-spin text-[#1a3d1c] [animation-duration:1s]'
              viewBox='0 0 24 24'
            >
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
                className='opacity-25'
              ></circle>
              <path
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                className='opacity-75'
              ></path>
            </svg>
          </div>
        </button>
      </form>
      {error || responseError ? (
        <div className='relative z-[2] mb-3 text-center text-red'>
          <FormError
            errors={[error, responseError].filter(Boolean)}
            scrollOnError={false}
            allowErrorMessages
          />
        </div>
      ) : null}
      <p className='relative z-[2] text-xs text-[rgba(255,255,255,0.4)]'>
        No spam. Unsubscribe anytime. We respect your privacy.
      </p>
    </>
  );
};

export default MidCtaForm;
